import Card from '@/components/projects/cards/Card';
import { ProjectTags } from '@/components/projects/cards/ProjectTags';
import ProjectUnit from '@/components/projects/cards/ProjectUnit';
import PublishDate from '@/components/projects/cards/PublishDate';
import Disabled from '@/components/shared/Disabled';
import TopicsContainer from '@/components/topics/TopicsContainer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import AvatarName from '@/components/ui/custom/dataDisplay/AvatarName';
import Text from '@/components/ui/custom/dataDisplay/Text';
import TextAreaInput from '@/components/ui/custom/dataInput/TextAreaInput';
import useHackathon from '@/data/hook/hackathon/useHackathon';
import { updateInfinityPages } from '@/data/hook/query/v1/useManualQuery';
import useManualSingleQuery from '@/data/hook/query/v1/useManualSingleQuery';
import useAuth from '@/data/hook/useAuth';
import { projectsQuerykeys } from '@/data/services/querykeys';
import { Project } from '@/models/Project';
import { hideParticipants, shouldHideParticipants } from '@/utils/hackathon';
import HackathonStagesPermission from '@/utils/permissions/HackathonStagesPermission';
import ProjectPermissions from '@/utils/permissions/ProjectPermission';
import { ROUTES } from '@/utils/routes';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { LEI_FELCA } from '@/lib/growthbook';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HackathonClassification } from '../cards/HackathonClassification';
import HackathonVotingAction from '../cards/HackathonVotingAction';
import ProjectDelete from './ProjectDelete';
import ProjectEdit from './ProjectEdit';
import ProjectInteractions from './ProjectInteractions';
import ProjectRuntime from './ProjectRuntime';
import { ProjectDescription } from './sections/ProjectDescription';
import { ProjectDocumentation } from './sections/ProjectDocumentation';
import { ProjectVideo } from './sections/ProjectVideo';

type ProjectViewProps = {
  project: Project;
};

export default function ProjectViewContent({ project }: ProjectViewProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'projects.card',
  });
  const { t: tField } = useTranslation('translation', {
    keyPrefix: 'projects.fields',
  });

  const { hackathon, hackathonsStages, isLoading } = useHackathon();
  const { user } = useAuth();

  const isLeiFelca = useFeatureIsOn(LEI_FELCA);
  const permissions = new ProjectPermissions({
    hackathon,
    user,
    model: project,
    isLoadingHackathon: isLoading,
    isLeiFelca,
  });

  const hackathonStagesPermission = new HackathonStagesPermission({
    user,
    model: hackathonsStages,
  });

  const displayProject =
    permissions.isProjectHackathon() &&
    shouldHideParticipants({
      stages: hackathonsStages,
      isLoadingStages: isLoading,
      userType: user?.userType,
    })
      ? hideParticipants(project)
      : project;

  const { set } = useManualSingleQuery(projectsQuerykeys.get(project.id));

  const queryClient = useQueryClient();

  const onMutate = useCallback(
    (project: Project) => {
      set(project);
      queryClient.getQueriesData({
        predicate: query => {
          const queryKey = query.queryKey as any[];
          if (queryKey[0] !== projectsQuerykeys.list().queryKey[0])
            return false;
          if (queryKey[1] !== projectsQuerykeys.list().queryKey[1])
            return false;
          if (!queryKey.at(-2)?.filters?.flagsId?.includes(hackathon?.flagId))
            return false;
          const data = updateInfinityPages(query.state.data as any, project);
          query.setData(data);
          return true;
        },
      });
    },
    [hackathon?.flagId, queryClient, set],
  );

  return (
    <div className="gap-3.5 w-full relative md:min-h-[calc(100vh-140px)] flex-col-reverse md:flex-row flex">
      <TopicsContainer className="sticky top-2.5 md:w-60 xl:w-96 lg:w-72 shrink-0 w-full flex flex-col gap-3 h-fit">
        <Card.Banner url={project.image} />
        <ConditionalRenderer condition={project.equipName}>
          <TextAreaInput
            label={tField('equipName.label')}
            labelPosition="top"
            className={{
              base: 'h-14',
              input:
                '!text-base-content font-400 text-wrap resize-none text-16',
            }}
            rows={2}
            value={project.equipName}
            disabled
          />
        </ConditionalRenderer>
        <Text text={t('equip')} className="text-14" />
        <ul className="flex flex-col gap-0.5 flex-wrap justify-end ml-1.5">
          {displayProject.users.map(user => (
            <AvatarName
              className="gap-1.5 w-full"
              user={user}
              textClassName="truncate"
              key={user.id}
              href={ROUTES.PROFILE.INFO(user.username)}
            />
          ))}
        </ul>
        <ConditionalRenderer condition={displayProject.teacherMentor}>
          <Text text={t('mentor')} className="text-14" />
          {displayProject.teacherMentor && (
            <AvatarName
              user={displayProject.teacherMentor}
              className="ml-1.5 gap-1.5"
            />
          )}
        </ConditionalRenderer>
        <ProjectTags flags={project.flags} tool={project.tool} />
        <ProjectUnit units={displayProject.units} />
        <div className="flex justify-between flex-wrap gap-2 items-center">
          <PublishDate publishAt={project.createdAt} className="mt-auto" />
          <div className="flex items-center gap-2">
            <ConditionalRenderer condition={permissions.canEdit()}>
              <ProjectEdit project={project} />
            </ConditionalRenderer>
            <ConditionalRenderer condition={permissions.canDelete()}>
              <ProjectDelete id={project.id} title={project.title} />
            </ConditionalRenderer>
          </div>
        </div>
      </TopicsContainer>
      <div className="flex flex-col grow gap-3 border-none h-full">
        <ConditionalRenderer
          condition={
            permissions.hasUnitPrivileges() && permissions.isProjectHackathon()
          }
        >
          <HackathonClassification
            project={project}
            onMutate={onMutate}
            permissions={hackathonStagesPermission}
          />
        </ConditionalRenderer>
        <TopicsContainer className="flex flex-col grow gap-3 border-none h-full bg-primary-content">
          <ConditionalRenderer condition={project.projectUrl}>
            <ProjectRuntime
              projectUrl={project.projectUrl}
              title={project.title}
              tool={project.tool}
            />
          </ConditionalRenderer>
          <ConditionalWrapper
            condition={isLeiFelca}
            wrapper={<Disabled className="w-full" />}
          >
            <ConditionalRenderer condition={permissions.canInteract()}>
              <ProjectInteractions project={project} />
            </ConditionalRenderer>
          </ConditionalWrapper>
          <ConditionalRenderer condition={permissions.isProjectHackathon()}>
            <div className="flex justify-end">
              <HackathonVotingAction project={project} onMutate={set} />
            </div>
          </ConditionalRenderer>
          <ProjectDescription description={project.description} />
          <ConditionalRenderer condition={project.documentation}>
            <ProjectDocumentation documentation={project.documentation} />
          </ConditionalRenderer>
          <ConditionalRenderer condition={project.video}>
            <ProjectVideo video={project.video} />
          </ConditionalRenderer>
        </TopicsContainer>
      </div>
    </div>
  );
}
